/*
  (c) 2026 Fluxonix Inc.
*/

import { useEffect, useRef, useState } from "react";

const API_BASE = "/public";

const EVENT_NAME = "Automate 2026";
const WORKFLOW_TYPE = "conference_discovery";
const CC_EMAIL = "sridhar@fluxonix.io";

const DB_NAME = "fluxonix_automate_capture_db";
const DB_VERSION = 1;
const PENDING_CAPTURE_STORE = "pending_captures";

function getFileExtension(mimeType = "") {
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("mp4")) return "mp4";
  if (mimeType.includes("m4a")) return "m4a";
  return "webm";
}

function createTimeline() {
  const now = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return [
    {
      label: "Conversation captured",
      time: now,
      detail: "Live Automate 2026 conversation recorded at the booth.",
    },
    {
      label: "Context preserved",
      time: now,
      detail: "Participant, company, role, and discussion context linked to the artifact.",
    },
    {
      label: "Discovery signal extracted",
      time: now,
      detail: "Pain points, workflows, decision rationale signals, and next steps identified.",
    },
    {
      label: "Summary delivered",
      time: now,
      detail: `Summary sent to participant and copied to ${CC_EMAIL}.`,
    },
  ];
}

function openCaptureDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(PENDING_CAPTURE_STORE)) {
        db.createObjectStore(PENDING_CAPTURE_STORE, { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function savePendingCapture(capture) {
  const db = await openCaptureDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(PENDING_CAPTURE_STORE, "readwrite");
    tx.objectStore(PENDING_CAPTURE_STORE).put(capture);

    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

async function deletePendingCapture(id) {
  const db = await openCaptureDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(PENDING_CAPTURE_STORE, "readwrite");
    tx.objectStore(PENDING_CAPTURE_STORE).delete(id);

    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

async function getPendingCaptures() {
  const db = await openCaptureDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(PENDING_CAPTURE_STORE, "readonly");
    const request = tx.objectStore(PENDING_CAPTURE_STORE).getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

async function uploadCapture(capture) {
  const mimeType = capture.audioMimeType || capture.audioBlob?.type || "audio/webm";
  const extension = getFileExtension(mimeType);

  const file = new File([capture.audioBlob], `${capture.id}.${extension}`, {
    type: mimeType,
  });

  const formData = new FormData();

  Object.entries(capture.metadata).forEach(([key, value]) => {
    formData.append(key, value || "");
  });

  formData.append("audio", file);

  const res = await fetch(`${API_BASE}/api/conference-capture`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error("Conference capture upload failed");
  }

  return res.json();
}

export default function AutomateCapturePage() {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");

  const [participantName, setParticipantName] = useState("");
  const [company, setCompany] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [email, setEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("Ready to capture");
  const [artifacts, setArtifacts] = useState([]);
  const [selectedArtifactId, setSelectedArtifactId] = useState(null);
  const [micStatus, setMicStatus] = useState("idle");
  // idle | listening | no_signal | noise_only | speech_detected | blocked
  const [micMessage, setMicMessage] = useState("");
  const [audioReviewed, setAudioReviewed] = useState(false);
  const [audioHealth, setAudioHealth] = useState({
    maxRms: 0,
    speechFrames: 0,
    noiseFrames: 0,
    silentFrames: 0,
  });

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const monitorIntervalRef = useRef(null);

  useEffect(() => {
    try {
      const savedArtifacts = localStorage.getItem("fluxonix_automate_artifacts");

      if (savedArtifacts) {
        const parsedArtifacts = JSON.parse(savedArtifacts);

        if (Array.isArray(parsedArtifacts) && parsedArtifacts.length > 0) {
          setArtifacts(parsedArtifacts);
          setSelectedArtifactId(parsedArtifacts[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to load Automate artifacts", err);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      retryPendingCaptures();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  useEffect(() => {
    return () => {
      stopAudioHealthMonitor();

      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  function startAudioHealthMonitor(stream) {
    const AudioContext = window.AudioContext || window.webkitAudioContext;

    if (!AudioContext) {
      setMicStatus("listening");
      setMicMessage("Recording started. Audio level detection is not supported in this browser.");
      return;
    }

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.fftSize = 2048;
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.fftSize);

    let maxRms = 0;
    let speechFrames = 0;
    let noiseFrames = 0;
    let silentFrames = 0;
    let elapsedFrames = 0;

    monitorIntervalRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(dataArray);

      let sumSquares = 0;

      for (let i = 0; i < dataArray.length; i += 1) {
        const normalized = (dataArray[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }

      const rms = Math.sqrt(sumSquares / dataArray.length);
      maxRms = Math.max(maxRms, rms);
      elapsedFrames += 1;

      const SILENCE_THRESHOLD = 0.008;
      const SPEECH_THRESHOLD = 0.035;

      if (rms < SILENCE_THRESHOLD) {
        silentFrames += 1;
      } else if (rms >= SPEECH_THRESHOLD) {
        speechFrames += 1;
      } else {
        noiseFrames += 1;
      }

      setAudioHealth({
        maxRms,
        speechFrames,
        noiseFrames,
        silentFrames,
      });

      if (elapsedFrames > 20 && maxRms < SILENCE_THRESHOLD) {
        setMicStatus("no_signal");
        setMicMessage("We can’t hear you. Check your microphone or unmute.");
        return;
      }

      if (elapsedFrames > 35 && speechFrames < 4 && noiseFrames > 10) {
        setMicStatus("noise_only");
        setMicMessage("We hear background noise, but no clear speech. Move closer to the mic.");
        return;
      }

      if (speechFrames >= 4) {
        setMicStatus("speech_detected");
        setMicMessage("Voice detected. Keep recording clearly.");
        return;
      }

      setMicStatus("listening");
      setMicMessage("Listening for your voice...");
    }, 100);
  }

  function stopAudioHealthMonitor() {
    if (monitorIntervalRef.current) {
      clearInterval(monitorIntervalRef.current);
      monitorIntervalRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
  }

  async function startRecording() {
    try {
      setAudioReviewed(false);
      setMicStatus("listening");
      setMicMessage("Listening for your voice...");
      setAudioHealth({
        maxRms: 0,
        speechFrames: 0,
        noiseFrames: 0,
        silentFrames: 0,
      });

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      micStreamRef.current = stream;
      startAudioHealthMonitor(stream);

      let mimeType = "";

      if (MediaRecorder.isTypeSupported("audio/webm")) {
        mimeType = "audio/webm";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else if (MediaRecorder.isTypeSupported("audio/m4a")) {
        mimeType = "audio/m4a";
      }

      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const finalType = mediaRecorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: finalType });

        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setStatus("Recording ready");
      };

      mediaRecorder.start();
      setRecording(true);
      setStatus("Recording conversation...");
    } catch (err) {
      console.error(err);
      stopAudioHealthMonitor();
      setRecording(false);
      setMicStatus("blocked");
      setMicMessage("Microphone unavailable. Please allow microphone access and try again.");
      setStatus("Microphone unavailable");
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    stopAudioHealthMonitor();
    setAudioReviewed(false);
    setRecording(false);

    if (audioHealth.speechFrames < 4) {
      setMicStatus("blocked");
      setMicMessage("This recording does not appear to contain usable speech. Please re-record.");
    }
  }

  function buildCapture() {
    const id = `AUTO-${Date.now()}`;

    const metadata = {
      id,
      local_id: id,
      event: EVENT_NAME,
      workflow_type: WORKFLOW_TYPE,
      participant_name: participantName,
      company,
      role_title: roleTitle,
      email,
      cc_email: CC_EMAIL,
    };

    return {
      id,
      metadata,
      audioBlob,
      audioMimeType: audioBlob?.type || "audio/webm",
      createdAt: new Date().toISOString(),
      syncStatus: "pending",
    };
  }

  function createArtifactFromResponse(data, capture) {
    return {
      id: data.submission_id || capture.id,
      title: `${company || "Automate"} Conversation Summary`,
      capturedAt: new Date().toLocaleString(),
      participantName,
      company,
      roleTitle,
      email,
      event: EVENT_NAME,
      html: data.html || data.summary_html || "<p>Conversation summary created.</p>",
      summary: data.summary || "",
      tags: data.tags || [],
      workflows: data.workflows_discussed || [],
      painPoints: data.pain_points || [],
      activeWound: data.active_wound || "",
      followUpAction: data.follow_up_action || "",
      timeline: createTimeline(),
      audioUrl,
      status: "Summary emailed",
    };
  }

  function persistArtifact(artifact) {
    setArtifacts((prev) => {
      const updated = [artifact, ...prev];

      localStorage.setItem(
        "fluxonix_automate_artifacts",
        JSON.stringify(updated.map((item) => ({ ...item, audioUrl: "" })))
      );

      return updated;
    });

    setSelectedArtifactId(artifact.id);
  }

  async function handleSubmit() {
    if (!audioBlob || !email) {
      alert("Please record audio and enter participant email.");
      return;
    }

    if (audioHealth.speechFrames < 4 || audioHealth.maxRms < 0.015) {
      alert("This recording does not appear to contain usable speech. Please re-record before creating a summary.");
      return;
    }

    if (!audioReviewed) {
      alert("Please play back the recording and confirm the voice is clear before submitting.");
      return;
    }

    setLoading(true);
    setStatus("Saving locally...");

    const capture = buildCapture();

    try {
      await savePendingCapture(capture);

      setStatus("Uploading and generating summary...");

      const data = await uploadCapture(capture);

      await deletePendingCapture(capture.id);

      const artifact = createArtifactFromResponse(data, capture);
      persistArtifact(artifact);

      setAudioBlob(null);
      setAudioUrl("");
      setAudioReviewed(false);
      setMicStatus("idle");
      setMicMessage("");
      setStatus("Summary emailed");
    } catch (err) {
      console.error(err);
      setStatus("Saved locally — pending sync");
    } finally {
      setLoading(false);
    }
  }

  async function retryPendingCaptures() {
    setStatus("Checking pending captures...");

    try {
      const pending = await getPendingCaptures();

      if (pending.length === 0) {
        setStatus("No pending captures");
        return;
      }

      for (const capture of pending) {
        setStatus(`Retrying ${capture.metadata.company || capture.id}...`);

        const data = await uploadCapture(capture);

        await deletePendingCapture(capture.id);

        const artifact = {
          id: data.submission_id || capture.id,
          title: `${capture.metadata.company || "Automate"} Conversation Summary`,
          capturedAt: new Date().toLocaleString(),
          participantName: capture.metadata.participant_name,
          company: capture.metadata.company,
          roleTitle: capture.metadata.role_title,
          email: capture.metadata.email,
          event: EVENT_NAME,
          html: data.html || data.summary_html || "<p>Conversation summary created.</p>",
          summary: data.summary || "",
          tags: data.tags || [],
          workflows: data.workflows_discussed || [],
          painPoints: data.pain_points || [],
          activeWound: data.active_wound || "",
          followUpAction: data.follow_up_action || "",
          timeline: createTimeline(),
          audioUrl: "",
          status: "Summary emailed",
        };

        persistArtifact(artifact);
      }

      setStatus("All pending captures synced");
    } catch (err) {
      console.error("Retry failed", err);
      setStatus("Some captures are still pending");
    }
  }

  const selectedArtifact =
    artifacts.find((artifact) => artifact.id === selectedArtifactId) || artifacts[0];

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-8">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-[420px_1fr]">
        <div className="rounded-2xl bg-white p-8 shadow-lg">
          <div className="mb-6">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
              Automate 2026
            </div>

            <h1 className="mb-2 text-2xl font-semibold">
              Capture the Conversation
            </h1>

            <p className="text-sm text-gray-500">
              Turn a live booth conversation into a structured discovery summary
              and send it before the context disappears.
            </p>
          </div>

          <div className="mb-8 flex flex-col items-center">
            <button
              onClick={recording ? stopRecording : startRecording}
              className={`flex h-24 w-24 items-center justify-center rounded-full text-xl text-white shadow-lg ${
                recording ? "bg-red-500" : "bg-blue-600"
              }`}
            >
              🎤
            </button>

            <p className="mt-3 text-sm text-gray-600">
              {recording
                ? "Recording..."
                : audioBlob
                ? "Recording ready"
                : "Tap to record"}
            </p>

            {audioUrl && <audio controls src={audioUrl} className="mt-4 w-full" />}

            {micMessage && (
              <div
                className={`mt-3 rounded-lg px-4 py-3 text-sm ${
                  micStatus === "speech_detected"
                    ? "border border-green-200 bg-green-50 text-green-800"
                    : micStatus === "no_signal" ||
                      micStatus === "noise_only" ||
                      micStatus === "blocked"
                    ? "border border-red-200 bg-red-50 text-red-800"
                    : "border border-blue-200 bg-blue-50 text-blue-800"
                }`}
              >
                {micMessage}
              </div>
            )}

            {audioUrl && (
              <div className="mt-4 w-full rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="mb-2 text-sm text-amber-900">Play Recording</p>

                <label className="flex items-center gap-2 text-sm text-amber-900">
                  <input
                    type="checkbox"
                    checked={audioReviewed}
                    onChange={(e) => setAudioReviewed(e.target.checked)}
                  />
                  I reviewed the recording and can clearly hear the conversation.
                </label>
              </div>
            )}
          </div>

          <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 px-3 py-3">
            <div className="mb-2 text-xs font-semibold text-blue-900">
              Try it.
            </div>

            <ul className="space-y-1 text-xs text-blue-900">
              <li className="flex gap-2">                
                <span>Describe a real problem your team dealt with recently.</span>
              </li>
              <li className="flex gap-2">
                <span>✓</span>
                <span>What happened?</span>
              </li>
              <li className="flex gap-2">
                <span>✓</span>
                <span>Who was involved?</span>
              </li>
              <li className="flex gap-2">
                <span>✓</span>
                <span>How was it resolved?</span>
              </li>
              <li className="flex gap-2">
                <span>✓</span>
                <span>What happened next?</span>
              </li>
              <li className="flex gap-2">
                <span>Speak naturally for 20-30 seconds.</span>
              </li>
            </ul>
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm text-gray-600">
              Participant Name
            </label>
            <input
              type="text"
              placeholder="Jane Smith"
              value={participantName}
              onChange={(e) => setParticipantName(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm text-gray-600">Company</label>
            <input
              type="text"
              placeholder="ABC Manufacturing"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm text-gray-600">
              Role / Title
            </label>
            <input
              type="text"
              placeholder="Quality Manager"
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm text-gray-600">
              Send summary to
            </label>
            <input
              type="email"
              placeholder="jane@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />

            <p className="mt-2 text-xs text-gray-500">
              Sridhar will be copied automatically.
            </p>
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full rounded-lg bg-blue-600 px-6 py-3 text-white shadow-md hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {loading ? "Creating Summary..." : "Create & Email Summary"}
          </button>

          <button
            onClick={retryPendingCaptures}
            className="mt-3 w-full rounded-lg border border-blue-600 px-6 py-3 text-blue-700 hover:bg-blue-50"
          >
            Retry Pending Uploads
          </button>

          <div className="mt-4 rounded-lg bg-gray-100 px-3 py-3 text-sm text-gray-700">
            Status: <span className="font-medium">{status}</span>
          </div>
        </div>

        <div className="rounded-2xl bg-white shadow-lg">
          <div className="border-b p-6">
            <h2 className="text-xl font-semibold">Conference Memory</h2>
            <p className="mt-1 text-sm text-gray-500">
              Structured discovery artifacts created from live Automate conversations.
            </p>
          </div>

          {artifacts.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-500">
              No conversation artifacts yet. Record a booth conversation to start
              building your Automate discovery ledger.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr]">
              <div className="border-r p-4">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Conversation Ledger
                </h3>

                <div className="space-y-3">
                  {artifacts.map((artifact) => (
                    <button
                      key={artifact.id}
                      onClick={() => setSelectedArtifactId(artifact.id)}
                      className={`w-full rounded-xl border p-4 text-left transition ${
                        selectedArtifact?.id === artifact.id
                          ? "border-blue-600 bg-blue-50"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="text-sm font-semibold">
                        {artifact.company || artifact.title}
                      </div>

                      <div className="mt-1 text-xs text-gray-500">
                        {artifact.participantName || "Unknown participant"} •{" "}
                        {artifact.capturedAt}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                          {artifact.event}
                        </span>

                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                          {artifact.status}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {selectedArtifact && (
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold">
                      {selectedArtifact.title}
                    </h3>

                    <p className="text-sm text-gray-500">
                      {selectedArtifact.id} • {selectedArtifact.capturedAt}
                    </p>
                  </div>

                  <section className="mb-8">
                    <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Conversation Timeline
                    </h4>

                    <div className="space-y-4">
                      {selectedArtifact.timeline?.map((item, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="h-3 w-3 rounded-full bg-blue-600" />
                            {index !== selectedArtifact.timeline.length - 1 && (
                              <div className="mt-1 h-full w-px bg-gray-200" />
                            )}
                          </div>

                          <div className="pb-4">
                            <div className="text-sm font-semibold">
                              {item.label}
                            </div>
                            <div className="text-xs text-gray-500">
                              {item.time}
                            </div>
                            <div className="mt-1 text-sm text-gray-700">
                              {item.detail}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="mb-8 rounded-xl border bg-gray-50 p-4">
                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Participant
                    </h4>

                    <div className="space-y-1 text-sm text-gray-700">
                      <div>
                        <strong>Name:</strong>{" "}
                        {selectedArtifact.participantName || "Unknown"}
                      </div>
                      <div>
                        <strong>Company:</strong>{" "}
                        {selectedArtifact.company || "Unknown"}
                      </div>
                      <div>
                        <strong>Role:</strong>{" "}
                        {selectedArtifact.roleTitle || "Unknown"}
                      </div>
                      <div>
                        <strong>Email:</strong>{" "}
                        {selectedArtifact.email || "Unknown"}
                      </div>
                    </div>
                  </section>

                  <section>
                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Conversation Summary
                    </h4>

                    <div className="rounded-xl border bg-white p-5">
                      <div
                        className="prose max-w-none text-sm [&_h3]:mt-6 [&_h3]:text-blue-600"
                        dangerouslySetInnerHTML={{ __html: selectedArtifact.html }}
                      />
                    </div>
                  </section>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}